import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Search, ChevronDown, Filter } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import MissileSimulationPopup from '../components/MissileSimulationPopup';
import {  industries } from '../components/data/data/Industries/IndustriesStaticData';
import { caseStudies } from '../components/data/data/CaseStudies/CaseStudiesData';
import CardShimmer from '../components/ShimmerEffects/CardShimmer';

const AllIndustries = () => {
  const navigate = useNavigate();
  const filterRef = useRef(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [showingCount, setShowingCount] = useState(9);
  const [expandedFilter, setExpandedFilter] = useState(null);
  const [showMobileFilter, setShowMobileFilter] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [imageLoadStates, setImageLoadStates] = useState({});
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  /* ONLY Industry opens */
  const toggleFilter = (filterName) => {
    if (filterName !== 'industry') return;
    setExpandedFilter(expandedFilter === filterName ? null : filterName);
  };

  const handleIndustryToggle = (industry) => {
    setSelectedIndustries((prev) =>
      prev.includes(industry)
        ? prev.filter((i) => i !== industry)
        : [...prev, industry]
    );
  };

  const handleCardClick = (study) => {
    if (study.isRestricted) {
      setIsPopupOpen(true);
    } else {
      navigate(study.route);
    }
  };
  /* Close mobile popup on outside click */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setShowMobileFilter(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredStudies = useMemo(() => {
    return caseStudies.filter((study) => {
      const matchesSearch =
        study.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        study.subtitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
        study.industry.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesIndustry =
        selectedIndustries.length === 0 ||
        selectedIndustries.includes(study.industry);

      return matchesSearch && matchesIndustry;
    });
  }, [searchQuery, selectedIndustries]);

  const handleImageLoad = (studyId) => {
    setImageLoadStates((prev) => ({
      ...prev,
      [studyId]: true,
    }));
  };
  useEffect(() => {
    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  }, []);

  const displayedStudies = filteredStudies.slice(0, showingCount);

  return (
    <div className="min-h-screen p-6 md:p-8 mt-28">
      {isPopupOpen && (
        <MissileSimulationPopup onClose={() => setIsPopupOpen(false)} />
      )}
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="md:ml-64">
          <h1 className="text-3xl md:text-4xl font-bold text-center text-[#D8D8D8] mb-8">
            Case Studies
          </h1>

          {/* Search + Mobile Filter */}
          <div
            className="mb-8 max-w-xl mx-auto flex gap-2 relative"
            ref={filterRef}
          >
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5" />
              <input
                type="text"
                placeholder="Search for a Case Study"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-[#31333A] text-white pl-12 pr-4 py-3 rounded-[50px] focus:outline-none placeholder:text-[#C1C1C1]"
              />
            </div>

            {/* Mobile Filter Icon */}
            <button
              onClick={() => setShowMobileFilter((prev) => !prev)}
              className="md:hidden bg-[#31333A] p-3 rounded-full text-white relative z-50"
            >
              <Filter className="w-5 h-5" />
            </button>

            {/* Mobile Dropdown */}
            {showMobileFilter && (
              <div className="absolute right-0 top-[56px] w-64 bg-[#31333A] rounded-xl shadow-xl p-4 z-40 md:hidden">
                <h3 className="text-white font-semibold mb-3">Filter</h3>

                <button
                  onClick={() => toggleFilter('industry')}
                  className="w-full flex justify-between items-center text-sm text-[#A7AAB5] mb-2"
                >
                  Industry
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${
                      expandedFilter === 'industry' ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {expandedFilter === 'industry' && (
                  <div className="space-y-2 max-h-56 overflow-y-auto custom-scrollbar">
                    {industries.map((industry) => (
                      <label
                        key={industry}
                        className="flex items-center gap-2 text-sm text-white"
                      >
                        <input
                          type="checkbox"
                          checked={selectedIndustries.includes(industry)}
                          onChange={() => handleIndustryToggle(industry)}
                          className="accent-[#EE2B2A]"
                        />
                        {industry}
                      </label>
                    ))}
                  </div>
                )}

                <div className="mt-4 text-sm text-[#6F7280] cursor-not-allowed">
                  Technology
                </div>
                <div className="mt-2 text-sm text-[#6F7280] cursor-not-allowed">
                  Date
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-8">
          {/* Sidebar Filters */}
          <div className="hidden md:block w-64 flex-shrink-0">
            <div className="p-4">
              <h3 className="font-semibold mb-4 flex items-center text-white gap-2">
                <span className="text-xl">=</span> Filter
              </h3>

              <button
                onClick={() => toggleFilter('industry')}
                className="w-full flex items-center justify-between py-2 text-sm font-medium text-[#A7AAB5]"
              >
                Industry
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${
                    expandedFilter === 'industry' ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {expandedFilter === 'industry' && (
                <div className="mt-2 space-y-2">
                  {industries.map((industry) => (
                    <label
                      key={industry}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedIndustries.includes(industry)}
                        onChange={() => handleIndustryToggle(industry)}
                        className="w-4 h-4 accent-[#EE2B2A]"
                      />
                      <span className="text-sm text-[#E3E6F0]">{industry}</span>
                    </label>
                  ))}
                </div>
              )}

              <div className="mt-4 text-sm text-[#6F7280] cursor-not-allowed">
                Technology
              </div>
              <div className="mt-2 text-sm text-[#6F7280] cursor-not-allowed">
                Date
              </div>
            </div>
          </div>

          {/* Cards */}
          <div className="flex-1 ">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 overflow-y-auto">
              {isLoading ? (
                // Show shimmer cards while loading
                <>
                  <CardShimmer imageAspectRatio="aspect-[4/3]" />
                  <CardShimmer imageAspectRatio="aspect-[4/3]" />
                  <CardShimmer imageAspectRatio="aspect-[4/3]" />
                  <CardShimmer
                    imageAspectRatio="aspect-[4/3]"
                    className="hidden sm:block"
                  />
                  <CardShimmer
                    imageAspectRatio="aspect-[4/3]"
                    className="hidden sm:block"
                  />
                  <CardShimmer
                    imageAspectRatio="aspect-[4/3]"
                    className="hidden lg:block"
                  />
                </>
              ) : (
                displayedStudies.map((study) => (
                  <div
                    key={study.id}
                    onClick={() => handleCardClick(study)}
                    className="bg-[#31333A] rounded-[20px] overflow-hidden cursor-pointer relative"
                  >
                    {/* Shimmer for individual card image */}
                    {!imageLoadStates[study.id] && (
                      <div className="absolute top-0 left-0 right-0 h-48">
                        <div className="relative overflow-hidden bg-gradient-to-r from-[#1A1C20] via-[#25272C] to-[#1A1C20] h-full">
                          <div className="absolute inset-0 -translate-x-full animate-shimmer">
                            <div className="h-full w-full bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="h-48">
                      <img
                        src={study.image}
                        alt={study.title}
                        className={`w-full h-full object-cover transition-opacity duration-500 ${
                          imageLoadStates[study.id]
                            ? 'opacity-100'
                            : 'opacity-0'
                        }`}
                        onLoad={() => handleImageLoad(study.id)}
                      />
                    </div>

                    <div className="p-4">
                      <h3 className="text-lg font-semibold text-white">
                        {study.title}
                      </h3>
                      <p className="text-[#D8D8D8] text-[13px] mb-4">
                        {study.subtitle}
                      </p>

                      <p className="text-[#D8D8D8] text-[12px] font-semibold font-jakarta mb-2">
                        <span className="border rounded-[5px] px-2 py-1 mr-2">
                          {study.tags[0]}
                        </span>
                        <span className="border rounded-[5px] px-2 py-1">
                          {study.tags[1]}
                        </span>
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* ✅ SHOW MORE RESTORED */}
            {filteredStudies.length > showingCount && (
              <div className="mt-8 text-center">
                <p className="text-[#FFFFFF] text-sm mb-4 font-light">
                  Showing {showingCount} of {filteredStudies.length}
                </p>
                <button
                  onClick={() => setShowingCount((prev) => prev + 9)}
                  className="px-12 py-2 border-2 border-[#EE2B2A] text-[#F5F5F5] rounded-[10px] font-bold"
                >
                  Show More
                </button>
              </div>
            )}

            {filteredStudies.length === 0 && (
              <div className="text-center py-16">
                <p className="text-gray-400 text-lg">
                  No case studies found matching your criteria.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllIndustries;
